import React, { useEffect, useRef, useState } from "react";
import { Combobox } from "@headlessui/react";
import { HiChevronUpDown } from "react-icons/hi2";

const inputClass =
  "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500";

// Searchable dropdown backed by a remote search: opens on click, and calls
// onQueryChange (debounced by the caller) as the user types. The caller owns
// fetching and passes the current result set back in as `items`.
const SearchableCombobox = ({
  items,
  value,
  onChange,
  getKey,
  getLabel,
  getSubLabel,
  placeholder,
  disabled = false,
  onQueryChange,
  loading = false,
  emptyLabel = "Nothing found",
}) => {
  const [query, setQuery] = useState("");
  const buttonRef = useRef(null);

  useEffect(() => {
    if (onQueryChange) onQueryChange(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <Combobox
      value={value}
      onChange={(item) => {
        onChange(item);
        setQuery("");
      }}
      disabled={disabled}
      nullable
      by={(a, b) => (a && b ? getKey(a) === getKey(b) : a === b)}
    >
      {({ open }) => (
        <div className="relative mt-1">
          <Combobox.Input
            className={`${inputClass} pr-9 ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
            displayValue={(item) => (item ? getLabel(item) : "")}
            onChange={(e) => setQuery(e.target.value)}
            onClick={() => {
              if (!open && !disabled) buttonRef.current?.click();
            }}
            placeholder={placeholder}
            autoComplete="off"
          />
          <Combobox.Button ref={buttonRef} className="absolute inset-y-0 right-0 flex items-center pr-2">
            <HiChevronUpDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {loading && <div className="select-none px-3 py-2 text-gray-500">Searching...</div>}

            {!loading && items.length === 0 && (
              <div className="select-none px-3 py-2 text-gray-500">{emptyLabel}</div>
            )}

            {!loading &&
              items.map((item) => (
                <Combobox.Option
                  key={getKey(item)}
                  value={item}
                  className={({ active }) =>
                    `cursor-pointer select-none px-3 py-2 ${active ? "bg-blue-600 text-white" : "text-gray-900"}`
                  }
                >
                  {({ selected, active }) => (
                    <div className="flex justify-between gap-3">
                      <span className={selected ? "font-semibold" : ""}>{getLabel(item)}</span>
                      {getSubLabel && getSubLabel(item) && (
                        <span className={`truncate ${active ? "text-blue-100" : "text-gray-500"}`}>
                          {getSubLabel(item)}
                        </span>
                      )}
                    </div>
                  )}
                </Combobox.Option>
              ))}
          </Combobox.Options>
        </div>
      )}
    </Combobox>
  );
};

export default SearchableCombobox;
